import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Col, FormGroup, Input, Label, Row, Spinner, Table } from "reactstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import moment from "moment";

import api from "../../../services/api";
import { useCompanies } from "../../../hooks/companies";
import { useToast } from "../../../hooks/toast";
import PageHero, { PageHeroRefreshButton } from "../../../components/PageHero";
import FilterBand from "../../../components/FilterBand";
import MetricCard from "../../../components/MetricCard";
import MetricCardGroup from "../../../components/MetricCardGroup";
import ShellCard, { TableSurface } from "../../../components/ShellCard";
import SortHeader from "../../../components/SortHeader";
import TablePagination from "../../../components/TablePagination";
import ExportarPesquisaButton from "../../../components/ExportarPesquisaButton";
import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import { reactSelectPortalProps, reactSelectStyles } from "../../../utils/reactSelectStyles";
import { filtrosDaUrl, urlComFiltros } from "../../../utils/filtrosUrl";
import { atalhosDePeriodo, descricaoPeriodo, validarPeriodo } from "../../../utils/periodoPesquisa";
import { formatDocument } from "../../../utils/formatDoc";
import NotaFiscalDetailModal from "./NotaFiscalDetailModal";
import {
  CHAVES_AVANCADAS,
  COM_CTE_OPTIONS,
  DEFINICAO_FILTROS,
  DIVERGENCIA_OPTIONS,
  MODALIDADE_FRETE_OPTIONS,
  PAGE_SIZES,
  SITUACAO_OPTIONS,
  STATUS_CALCULO_OPTIONS,
  STATUS_TONE,
  TETO_DIAS,
  TIPO_DATA_OPTIONS,
  TIPO_OPERACAO_OPTIONS,
  UF_OPTIONS,
  buscaDireta,
  comChaveParcial,
  paramsDaApi,
} from "./filtros";

import "./styles.css";
import "../PesquisaCte/styles.css";

// Consulta NFe (Flow › Consultas e Trackfy › Operação). Em 31/10/2026 a
// Pesquisa Importação (PO1-2967, épico PO1-2280) e a Consulta NFe antiga viraram
// esta tela só: filtros do núcleo da "Pesquisa Importação" do FLOW, período por
// emissão ou importação, ordenação no servidor e exportação em segundo plano,
// mais o que só a antiga tinha (rota, negócio/serviço, divergência, vínculo com
// CT-e, copiar a chave). Detalhe com Etiqueta, Vínculos e Ocorrências.
// Somente leitura.

const emptyResult = {
  items: [],
  resumo: { total: 0, calculadas: 0, divergentes: 0, semCte: 0, valorNotas: 0, valorFreteSimulado: 0 },
  paginacao: { pagina: 1, tamanhoPagina: 25, total: 0, totalPaginas: 0 },
};

const fmtNumber = (value) => new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(Number(value || 0));
const fmtCurrency = (value) =>
  value == null ? "-" : new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value || 0));
// Emissão da NF vem como meia-noite UTC: formata em UTC para não cair no dia anterior.
const fmtDate = (value) => (value ? moment.utc(value).format("DD/MM/YYYY") : "-");
const fmtDateTime = (value) => (value ? moment(value).format("DD/MM/YYYY HH:mm") : "-");

// Ordem fixa: as larguras por coluna ficam em PesquisaCte/styles.css
// (.pesquisa-importacao-table, por nth-child).
const TABLE_COLUMNS = [
  { key: "numero", label: "NFe", sortField: "numero" },
  { key: "emissao", label: "Emissão", sortField: "emissao" },
  { key: "importacao", label: "Importação", sortField: "importacao" },
  { key: "transportadora", label: "Transportadora", sortField: "transportadora" },
  { key: "rota", label: "Rota" },
  { key: "destinatario", label: "Destinatário" },
  { key: "tomador", label: "Tomador" },
  { key: "valorNota", label: "Valor NFe", sortField: "valorNota", numeric: true },
  { key: "freteSimulado", label: "Frete simulado", sortField: "freteSimulado", numeric: true },
  { key: "calculo", label: "Cálculo / situação" },
  { key: "operacao", label: "Operação / modalidade" },
  { key: "negocio", label: "Negócio / serviço" },
  { key: "quantidadeCtes", label: "CT-e", numeric: true },
  { key: "arquivo", label: "Arquivo" },
];

const routeLabel = (item) => {
  const origem = [item.cidadeOrigem, item.ufOrigem].filter(Boolean).join("/");
  const destino = [item.cidadeDestino, item.ufDestino].filter(Boolean).join("/");
  if (!origem && !destino) return "-";
  return `${origem || "-"} → ${destino || "-"}`;
};

const fieldInputStyle = {
  background: AUTOPILOT_COLORS.inputBg,
  borderColor: AUTOPILOT_COLORS.inputBorder,
  color: AUTOPILOT_COLORS.title,
  boxShadow: "none",
};

const quickButtonStyle = (active) => ({
  borderRadius: 999,
  border: `1px solid ${active ? AUTOPILOT_COLORS.accent : AUTOPILOT_COLORS.border}`,
  background: active ? "var(--theme-accent-glow)" : AUTOPILOT_COLORS.inputBg,
  color: active ? AUTOPILOT_COLORS.title : AUTOPILOT_COLORS.text,
  fontSize: "0.78rem",
  fontWeight: 600,
  padding: "0.38rem 0.75rem",
  cursor: "pointer",
  boxShadow: active ? "var(--everlog-card-interactive-shadow)" : "none",
});

const selectValue = (options, value) => options.find((option) => String(option.value) === String(value == null ? "" : value)) || options[0];
const ATALHOS = atalhosDePeriodo(TETO_DIAS);

const mensagemErro = (requestError, padrao) => {
  const erro = requestError && requestError.response && requestError.response.data && requestError.response.data.error;
  if (typeof erro === "string") return erro;
  if (erro && typeof erro === "object") return Object.values(erro).join(" ");
  return padrao;
};

const ConsultaNotasFiscais = () => {
  const { empresaId, transportadoraId, optionsTransportadora } = useCompanies();
  const { addToast } = useToast();
  const history = useHistory();
  const location = useLocation();

  const [filters, setFilters] = useState(() => filtrosDaUrl(location.search, DEFINICAO_FILTROS));
  const [draftFilters, setDraftFilters] = useState(() => filtrosDaUrl(location.search, DEFINICAO_FILTROS));
  const [cidadeOption, setCidadeOption] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [result, setResult] = useState(emptyResult);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [periodoErro, setPeriodoErro] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const cidadeTimerRef = useRef(null);

  useEffect(() => {
    const atual = location.search || "";
    const nova = urlComFiltros(atual, filters, DEFINICAO_FILTROS);
    if (nova !== atual) history.replace({ pathname: location.pathname, search: nova });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => () => {
    if (cidadeTimerRef.current) clearTimeout(cidadeTimerRef.current);
  }, []);

  const params = useMemo(() => paramsDaApi(filters, { embarcadorId: empresaId, transportadoraId }), [empresaId, filters, transportadoraId]);
  const paramsExportacao = useMemo(
    () => paramsDaApi(filters, { embarcadorId: empresaId, transportadoraId }, { paraExportar: true }),
    [empresaId, filters, transportadoraId]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/pesquisa/notas-fiscais", { params });
      setResult(response.data || emptyResult);
    } catch (requestError) {
      setResult(emptyResult);
      setError(mensagemErro(requestError, "Não foi possível pesquisar as NFes."));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  // Troca de empresa/transportadora no cabeçalho volta para a página 1 (não na
  // montagem: a página pode ter vindo na URL compartilhada).
  const montouRef = useRef(false);
  useEffect(() => {
    if (!montouRef.current) {
      montouRef.current = true;
      return;
    }
    setFilters((current) => (current.pagina === 1 ? current : { ...current, pagina: 1 }));
  }, [empresaId, transportadoraId]);

  const commitFilters = useCallback(
    (next, { manterPagina = false } = {}) => {
      const erro = validarPeriodo(
        { dataInicial: next.dataInicial, dataFinal: next.dataFinal, buscaDireta: buscaDireta(next), comChaveParcial: comChaveParcial(next) },
        TETO_DIAS
      );
      setPeriodoErro(erro || "");
      if (erro) {
        addToast({ place: "tr", type: "warning", title: "Período inválido", description: erro, icon: "tim-icons icon-alert-circle-exc" });
        return false;
      }
      const safe = manterPagina ? { ...next } : { ...next, pagina: 1 };
      setDraftFilters(safe);
      setFilters(safe);
      return true;
    },
    [addToast]
  );

  const copiarChave = useCallback(
    (chave) => {
      if (!chave || !navigator.clipboard) return;
      navigator.clipboard.writeText(chave).then(() => {
        addToast({ place: "tr", type: "success", title: "Chave de acesso copiada", description: chave, icon: "tim-icons icon-single-copy-04" });
      });
    },
    [addToast]
  );

  // Cards clicáveis: filtram a lista; clicar de novo desfaz (como na Consulta NFe antiga).
  const toggleTileFilter = (key, value) => commitFilters({ ...draftFilters, [key]: filters[key] === value ? "" : value });
  const clearTileFilters = () => commitFilters({ ...draftFilters, comCte: "", divergencia: "" });

  const updateDraft = (field, value) => setDraftFilters((current) => ({ ...current, [field]: value }));
  const handleApply = () => commitFilters({ ...draftFilters });
  const resetFilters = () => {
    setPeriodoErro("");
    setCidadeOption(null);
    const limpos = filtrosDaUrl("", DEFINICAO_FILTROS);
    setDraftFilters(limpos);
    setFilters(limpos);
  };
  const applyShortcut = (shortcut) => commitFilters({ ...draftFilters, ...shortcut.getDates() });

  const handleSort = (field) => {
    const dir = filters.ordenarPor === field && filters.ordem === "desc" ? "asc" : "desc";
    commitFilters({ ...filters, ordenarPor: field, ordem: dir });
  };
  const handlePageChange = (pagina) => commitFilters({ ...filters, pagina }, { manterPagina: true });
  const handlePageSizeChange = (tamanhoPagina) => commitFilters({ ...filters, tamanhoPagina });

  // Cidade de destino: autocomplete por UF (mesma rota do Sourcing, /cidades).
  const loadCidadeOptions = useCallback(
    (inputValue) =>
      new Promise((resolve) => {
        if (cidadeTimerRef.current) clearTimeout(cidadeTimerRef.current);
        if (!draftFilters.ufDestino) {
          resolve([]);
          return;
        }
        cidadeTimerRef.current = setTimeout(async () => {
          try {
            const res = await api.get("/cidades", { params: { uf: draftFilters.ufDestino, q: inputValue || "" } });
            resolve((res.data || []).map((c) => ({ value: c.id, label: c.nome })));
          } catch {
            resolve([]);
          }
        }, 250);
      }),
    [draftFilters.ufDestino]
  );

  const activeFiltersCount = useMemo(() => {
    const avancados = CHAVES_AVANCADAS.filter((key) => filters[key] !== null && filters[key] !== undefined && filters[key] !== "").length;
    const periodo = filters.dataInicial || filters.dataFinal || filters.tipoData !== "emissao" ? 1 : 0;
    return avancados + periodo;
  }, [filters]);

  const activeShortcut = useMemo(() => {
    const match = ATALHOS.find((shortcut) => {
      const dates = shortcut.getDates();
      return dates.dataInicial === draftFilters.dataInicial && dates.dataFinal === draftFilters.dataFinal;
    });
    return match ? match.label : null;
  }, [draftFilters.dataInicial, draftFilters.dataFinal]);

  const summary = result.resumo || emptyResult.resumo;
  const pagination = result.paginacao || emptyResult.paginacao;
  const items = result.items || [];
  const sort = { field: filters.ordenarPor, dir: filters.ordem };

  const transportadoraOptions = useMemo(
    () => [{ value: "", label: "Todas" }].concat((optionsTransportadora || []).map((t) => ({ value: String(t.value), label: t.label }))),
    [optionsTransportadora]
  );

  return (
    <div className="content nfe-consulta-page pesquisa-importacao-page">
      <PageHero
        eyebrow={location.pathname.indexOf("/trackfy/") >= 0 ? "Operação" : "Consultas"}
        title="Consulta NFe"
        description="Localize as notas fiscais por emissão ou importação e veja rota, cálculo do frete, vínculo com CT-e, arquivo de origem, etiqueta e ocorrências de cada uma, sem aprovar nem conciliar."
        actions={
          <>
            <PageHeroRefreshButton onClick={load} loading={loading} />
            <ExportarPesquisaButton tipo="nf" filtros={paramsExportacao} disabled={loading || !pagination.total} />
          </>
        }
      />

      <FilterBand
        mode="server"
        searchLabel="Chave de acesso"
        searchHint={descricaoPeriodo({ ...filters, buscaDireta: buscaDireta(filters) })}
        searchPlaceholder="Chave de acesso da NFe (44 dígitos ou parte)"
        searchValue={draftFilters.chave}
        onSearchChange={(value) => updateDraft("chave", value)}
        onSearchSubmit={(value) => commitFilters({ ...draftFilters, chave: value })}
        activeFiltersCount={activeFiltersCount}
        open={advancedOpen}
        onToggle={setAdvancedOpen}
        panelTitle="Filtros"
        panelDescription={`Período por emissão ou importação (até ${TETO_DIAS} dias; com a chave completa ou o número o período é ignorado; com parte da chave, até 31 dias), identificação, partes, cálculo, divergência, vínculo com CT-e, operação, modalidade, situação e destino.`}
        onApply={handleApply}
        onClear={resetFilters}
        busy={loading}
      >
        <Row>
          <Col md="2">
            <FormGroup>
              <Label>Data</Label>
              <Select classNamePrefix="react-select" styles={reactSelectStyles} {...reactSelectPortalProps()} isSearchable={false} options={TIPO_DATA_OPTIONS} value={selectValue(TIPO_DATA_OPTIONS, draftFilters.tipoData)} onChange={(option) => updateDraft("tipoData", option ? option.value : "emissao")} aria-label="Tipo de data" />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label for="cnfe-data-inicial">Data inicial</Label>
              <Input id="cnfe-data-inicial" type="date" value={draftFilters.dataInicial} onChange={(e) => updateDraft("dataInicial", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label for="cnfe-data-final">Data final</Label>
              <Input id="cnfe-data-final" type="date" value={draftFilters.dataFinal} onChange={(e) => updateDraft("dataFinal", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label>Atalhos</Label>
              <div className="nfe-consulta-shortcuts">
                {ATALHOS.map((shortcut) => (
                  <button key={shortcut.label} type="button" style={quickButtonStyle(activeShortcut === shortcut.label)} onClick={() => applyShortcut(shortcut)} disabled={loading}>
                    {shortcut.label}
                  </button>
                ))}
              </div>
            </FormGroup>
          </Col>
        </Row>
        {periodoErro ? <div className="pesquisa-doc-periodo-erro" data-testid="cnfe-periodo-erro">{periodoErro}</div> : null}

        <Row>
          <Col md="2">
            <FormGroup>
              <Label for="cnfe-numero">Número</Label>
              <Input id="cnfe-numero" value={draftFilters.numero} onChange={(e) => updateDraft("numero", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
          <Col md="1">
            <FormGroup>
              <Label for="cnfe-serie">Série</Label>
              <Input id="cnfe-serie" value={draftFilters.serie} onChange={(e) => updateDraft("serie", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label for="cnfe-pedido">Pedido</Label>
              <Input id="cnfe-pedido" value={draftFilters.pedido} onChange={(e) => updateDraft("pedido", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label for="cnfe-numero-cte">Número do CT-e</Label>
              <Input id="cnfe-numero-cte" value={draftFilters.numeroCte} onChange={(e) => updateDraft("numeroCte", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <Label>Transportadora</Label>
              <Select classNamePrefix="react-select" styles={reactSelectStyles} {...reactSelectPortalProps()} options={transportadoraOptions} isDisabled={Boolean(transportadoraId)} value={selectValue(transportadoraOptions, draftFilters.transportadoraId)} onChange={(option) => updateDraft("transportadoraId", option && option.value ? Number(option.value) : null)} aria-label="Transportadora" />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label for="cnfe-cnpj-tomador">CNPJ do tomador</Label>
              <Input id="cnfe-cnpj-tomador" value={draftFilters.cnpjTomador} onChange={(e) => updateDraft("cnpjTomador", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="3">
            <FormGroup>
              <Label for="cnfe-destinatario">Destinatário (CNPJ/CPF ou nome)</Label>
              <Input id="cnfe-destinatario" value={draftFilters.destinatario} onChange={(e) => updateDraft("destinatario", e.target.value)} style={fieldInputStyle} />
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <Label>Status do cálculo</Label>
              <Select classNamePrefix="react-select" styles={reactSelectStyles} {...reactSelectPortalProps()} isSearchable={false} options={STATUS_CALCULO_OPTIONS} value={selectValue(STATUS_CALCULO_OPTIONS, draftFilters.statusCalculo)} onChange={(option) => updateDraft("statusCalculo", option ? option.value : "")} aria-label="Status do cálculo" />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label>Tipo de operação</Label>
              <Select classNamePrefix="react-select" styles={reactSelectStyles} {...reactSelectPortalProps()} isSearchable={false} options={TIPO_OPERACAO_OPTIONS} value={selectValue(TIPO_OPERACAO_OPTIONS, draftFilters.tipoOperacao)} onChange={(option) => updateDraft("tipoOperacao", option ? option.value : "")} aria-label="Tipo de operação" />
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label>Modalidade de frete</Label>
              <Select classNamePrefix="react-select" styles={reactSelectStyles} {...reactSelectPortalProps()} isSearchable={false} options={MODALIDADE_FRETE_OPTIONS} value={selectValue(MODALIDADE_FRETE_OPTIONS, draftFilters.modalidadeFrete)} onChange={(option) => updateDraft("modalidadeFrete", option ? option.value : "")} aria-label="Modalidade de frete" />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="3">
            <FormGroup>
              <Label>Situação de faturamento</Label>
              <Select classNamePrefix="react-select" styles={reactSelectStyles} {...reactSelectPortalProps()} isSearchable={false} options={SITUACAO_OPTIONS} value={selectValue(SITUACAO_OPTIONS, draftFilters.situacaoId)} onChange={(option) => updateDraft("situacaoId", option ? option.value : "")} aria-label="Situação de faturamento" />
            </FormGroup>
          </Col>
          <Col md="2">
            <FormGroup>
              <Label>UF de destino</Label>
              <Select
                classNamePrefix="react-select"
                styles={reactSelectStyles}
                {...reactSelectPortalProps()}
                options={UF_OPTIONS}
                value={selectValue(UF_OPTIONS, draftFilters.ufDestino)}
                onChange={(option) => {
                  setCidadeOption(null);
                  setDraftFilters((current) => ({ ...current, ufDestino: option ? option.value : "", cidadeDestinoId: null }));
                }}
                aria-label="UF de destino"
              />
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label>Cidade de destino</Label>
              <AsyncSelect
                classNamePrefix="react-select"
                styles={reactSelectStyles}
                {...reactSelectPortalProps()}
                cacheOptions={false}
                defaultOptions
                isClearable
                isDisabled={!draftFilters.ufDestino}
                loadOptions={loadCidadeOptions}
                value={cidadeOption}
                placeholder={draftFilters.ufDestino ? "Digite a cidade" : "Escolha a UF primeiro"}
                noOptionsMessage={() => "Nenhuma cidade"}
                onChange={(option) => {
                  setCidadeOption(option || null);
                  updateDraft("cidadeDestinoId", option ? Number(option.value) : null);
                }}
                aria-label="Cidade de destino"
              />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="3">
            <FormGroup>
              <Label>Divergência</Label>
              <Select classNamePrefix="react-select" styles={reactSelectStyles} {...reactSelectPortalProps()} isSearchable={false} options={DIVERGENCIA_OPTIONS} value={selectValue(DIVERGENCIA_OPTIONS, draftFilters.divergencia)} onChange={(option) => updateDraft("divergencia", option ? option.value : "")} aria-label="Divergência" />
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <Label>Vínculo com CT-e</Label>
              <Select classNamePrefix="react-select" styles={reactSelectStyles} {...reactSelectPortalProps()} isSearchable={false} options={COM_CTE_OPTIONS} value={selectValue(COM_CTE_OPTIONS, draftFilters.comCte)} onChange={(option) => updateDraft("comCte", option ? option.value : "")} aria-label="Vínculo com CT-e" />
            </FormGroup>
          </Col>
        </Row>
      </FilterBand>

      <MetricCardGroup title="Resumo do recorte" description="Clique em Com CT-e, Sem CT-e ou Divergentes para filtrar a lista; clique de novo para desfazer.">
        <MetricCard label="NFes no recorte" value={fmtNumber(summary.total)} helper={fmtCurrency(summary.valorNotas)} iconClassName="tim-icons icon-notes" tone="info" active={!filters.comCte && !filters.divergencia} onClick={clearTileFilters} />
        <MetricCard
          label="Com CT-e"
          value={fmtNumber(Math.max(0, Number(summary.total || 0) - Number(summary.semCte || 0)))}
          helper={`${summary.total ? Math.round(((summary.total - summary.semCte) / summary.total) * 100) : 0}% do recorte`}
          iconClassName="tim-icons icon-check-2"
          tone="success"
          active={filters.comCte === "sim"}
          onClick={() => toggleTileFilter("comCte", "sim")}
        />
        <MetricCard label="Sem CT-e" value={fmtNumber(summary.semCte)} helper="aguardando vínculo" iconClassName="tim-icons icon-link-72" tone="warning" active={filters.comCte === "nao"} onClick={() => toggleTileFilter("comCte", "nao")} />
        <MetricCard label="Divergentes" value={fmtNumber(summary.divergentes)} helper="frete do CT-e diferente do simulado" iconClassName="tim-icons icon-alert-circle-exc" tone="danger" active={filters.divergencia === "sim"} onClick={() => toggleTileFilter("divergencia", "sim")} />
        <MetricCard label="Frete simulado" value={fmtCurrency(summary.valorFreteSimulado)} helper={`${fmtNumber(summary.calculadas)} NFe(s) calculada(s)`} iconClassName="tim-icons icon-delivery-fast" tone="neutral" />
      </MetricCardGroup>

      <ShellCard className="nfe-consulta-table-card" title="Notas fiscais encontradas" description={`${fmtNumber(pagination.total)} resultado(s)`}>
        <TableSurface>
          {error ? <div className="nfe-consulta-state nfe-consulta-state--error" data-testid="cnfe-erro">{String(error)}</div> : null}
          {!error && loading ? (
            <div className="nfe-consulta-state">
              <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} /> Pesquisando NFes...
            </div>
          ) : null}
          {!error && !loading && items.length === 0 ? <div className="nfe-consulta-state">Nenhuma NFe encontrada para os filtros informados.</div> : null}

          {!error && !loading && items.length > 0 ? (
            <Table className="nfe-consulta-table pesquisa-importacao-table hub-thead-accent" size="sm" responsive hover data-testid="cnfe-tabela">
              <thead className="text-primary">
                <tr>
                  {TABLE_COLUMNS.map((col) => (
                    <th key={col.key} className={col.numeric ? "is-number" : undefined}>
                      {col.sortField ? <SortHeader label={col.label} field={col.sortField} sort={sort} onSort={handleSort} align={col.numeric ? "right" : "left"} /> : col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const tone = item.divergencia ? "danger" : STATUS_TONE[Number(item.statusCalculo)] || "neutral";
                  return (
                    <tr key={item.id} data-testid="cnfe-linha">
                      <td>
                        <button type="button" className="nfe-consulta-document-link" onClick={() => setSelectedId(item.id)}>
                          {item.numero} / {item.serie}
                        </button>
                        <small className="nfe-consulta-chave" title={item.chave || undefined}>
                          <span className="nfe-consulta-chave__valor">{item.chave || "Sem chave"}</span>
                          {item.chave ? (
                            <button type="button" className="nfe-consulta-chave__copiar" onClick={() => copiarChave(item.chave)} title="Copiar chave de acesso" aria-label="Copiar chave de acesso">
                              <i className="tim-icons icon-single-copy-04" />
                            </button>
                          ) : null}
                        </small>
                      </td>
                      <td className="nfe-consulta-nowrap">{fmtDate(item.emissao)}</td>
                      <td className="nfe-consulta-nowrap">{fmtDateTime(item.importacao)}</td>
                      <td><strong>{item.transportadora || "-"}</strong></td>
                      <td className="nfe-consulta-route-cell">{routeLabel(item)}</td>
                      <td>
                        {item.destinatario || "-"}
                        {item.destinatarioCnpj ? <span className="pesquisa-doc-sub">{formatDocument(item.destinatarioCnpj)}</span> : null}
                      </td>
                      <td>{item.tomadorCnpj ? formatDocument(item.tomadorCnpj) : "-"}</td>
                      <td className="is-number">{fmtCurrency(item.valorNota)}</td>
                      <td className="is-number">{fmtCurrency(item.valorFreteSimulado)}</td>
                      <td>
                        <span className={`nfe-consulta-status nfe-consulta-status--${tone}`}>{item.divergencia ? "Divergente" : item.statusCalculoLabel || "-"}</span>
                        <small>{item.situacao || "-"}</small>
                      </td>
                      <td>
                        <strong>{item.tipoOperacaoLabel || "-"}</strong>
                        <small>{item.modalidadeFreteLabel || "-"}</small>
                      </td>
                      <td>
                        <strong>{item.negocio || "-"}</strong>
                        <small>{item.tipoServico || "-"}</small>
                      </td>
                      <td className="is-number">{fmtNumber(item.quantidadeCtes)}</td>
                      <td><small className="nfe-consulta-muted" title={item.arquivo || undefined}>{item.arquivo || "-"}</small></td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : null}

          <TablePagination page={pagination.pagina || filters.pagina} pageSize={filters.tamanhoPagina} total={pagination.total || 0} pageSizes={PAGE_SIZES} itemLabel="NFe(s)" disabled={loading} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} />
        </TableSurface>
      </ShellCard>

      <NotaFiscalDetailModal isOpen={Boolean(selectedId)} notaFiscalId={selectedId} embarcadorId={empresaId} transportadoraId={transportadoraId} onClose={() => setSelectedId(null)} mostrarOcorrencias />
    </div>
  );
};

export default ConsultaNotasFiscais;
